/**
 * RingBoundaries — the AI score mode's controls: how many rings (2-5) and
 * where each one starts.
 *
 * One `<input type="range">` per boundary, stacked over a single track and a
 * histogram of the active lists' scores (0.05 bins, each bar in the colour of
 * the ring it falls in), so moving a thumb shows who moves with it. The inputs
 * are transparent except for their thumbs, so only a thumb takes the pointer;
 * the keyboard moves the focused one by 0.05. The clamping (order, a 0.05 gap,
 * 1.00-3.00) is `clampBound`'s.
 *
 * The histogram is SVG, so each bar's height is a `height` attribute rather
 * than a computed `style`.
 */
import type { BullseyeSettings } from "@nu-assist/contracts";

import {
  type HistogramBin,
  MAX_RINGS,
  MAX_SCORE,
  MIN_RINGS,
  MIN_SCORE,
  SCORE_STEP,
  f2,
  ringTone,
} from "./bullseyeGeometry";
import { monthDay, nameFromEmail } from "./bullseyeFormat";
import { RING_BG, RING_FILL } from "./bullseyeTones";

export interface RingBoundariesProps {
  bounds: readonly number[];
  bins: readonly HistogramBin[];
  /** The saved row, for "Boundaries set by …". */
  saved: BullseyeSettings | undefined;
  /** Present mode hides who set the boundaries, as it hides who ran each review. */
  present: boolean;
  /** Server messages for single boundaries (a 422's field errors), by index. */
  boundErrors: ReadonlyMap<number, string>;
  onRingCount: (k: number) => void;
  onBound: (index: number, value: number) => void;
  onReset: () => void;
}

const HIST_W = 410;
const HIST_H = 56;

const RANGE_THUMB = [
  "[&::-webkit-slider-thumb]:pointer-events-auto",
  "[&::-webkit-slider-thumb]:h-4",
  "[&::-webkit-slider-thumb]:w-4",
  "[&::-webkit-slider-thumb]:cursor-grab",
  "[&::-webkit-slider-thumb]:appearance-none",
  "[&::-webkit-slider-thumb]:rounded-full",
  "[&::-webkit-slider-thumb]:mt-0.5",
  // Preflight's `border-style: solid` does not reach a pseudo-element: without
  // this the border is 0 wide and the white thumb vanishes on the white card.
  "[&::-webkit-slider-thumb]:border-solid",
  "[&::-webkit-slider-thumb]:border-2",
  "[&::-webkit-slider-thumb]:border-accent",
  "[&::-webkit-slider-thumb]:bg-surface",
  "[&::-moz-range-thumb]:pointer-events-auto",
  "[&::-moz-range-thumb]:h-3",
  "[&::-moz-range-thumb]:w-3",
  "[&::-moz-range-thumb]:cursor-grab",
  "[&::-moz-range-thumb]:rounded-full",
  "[&::-moz-range-thumb]:border-solid",
  "[&::-moz-range-thumb]:border-2",
  "[&::-moz-range-thumb]:border-accent",
  "[&::-moz-range-thumb]:bg-surface",
  "[&::-webkit-slider-runnable-track]:h-5",
  "[&::-webkit-slider-runnable-track]:bg-transparent",
  "[&::-moz-range-track]:bg-transparent",
].join(" ");

export function RingBoundaries({
  bounds,
  bins,
  saved,
  present,
  boundErrors,
  onRingCount,
  onBound,
  onReset,
}: RingBoundariesProps) {
  const k = bounds.length;
  const max = Math.max(1, ...bins.map((b) => b.count));
  const barW = HIST_W / bins.length;

  return (
    <section
      className="flex flex-col gap-2.5 rounded-md border border-border bg-surface p-3.5 shadow-sm"
      data-testid="bullseye-bounds"
    >
      <h3 className="text-caption font-semibold uppercase tracking-wider text-text-secondary">
        Ring boundaries
      </h3>

      <div className="flex items-center gap-2 text-label text-text-secondary">
        <span>Rings</span>
        <button
          type="button"
          aria-label="Fewer rings"
          disabled={k <= MIN_RINGS}
          onClick={() => onRingCount(k - 1)}
          className="h-7 w-7 rounded-sm border border-border bg-surface text-body text-text hover:border-border-hover disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          data-testid="bullseye-rings-minus"
        >
          −
        </button>
        <output
          aria-live="polite"
          className="min-w-5 text-center font-bold tabular-nums text-text"
          data-testid="bullseye-rings-n"
        >
          {k}
        </output>
        <button
          type="button"
          aria-label="More rings"
          disabled={k >= MAX_RINGS}
          onClick={() => onRingCount(k + 1)}
          className="h-7 w-7 rounded-sm border border-border bg-surface text-body text-text hover:border-border-hover disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          data-testid="bullseye-rings-plus"
        >
          +
        </button>
      </div>

      <div className="relative h-20">
        <svg
          viewBox={`0 0 ${HIST_W} ${HIST_H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-14 w-full"
          data-testid="bullseye-histogram"
        >
          {bins.map((bin, i) => {
            const h = bin.count === 0 ? 0 : Math.max(1, (bin.count / max) * HIST_H);
            return (
              <rect
                key={bin.value}
                x={i * barW + 0.5}
                y={HIST_H - h}
                width={Math.max(0.5, barW - 1)}
                height={h}
                rx={1.5}
                className={bin.band === null ? "fill-border" : RING_FILL[ringTone(bin.band, k)]}
                data-band={bin.band ?? "out"}
              />
            );
          })}
        </svg>
        <div className="absolute inset-x-0 bottom-2 h-1 rounded-full bg-border" />
        {bounds.map((v, i) => (
          <input
            // The index IS the identity: ring i's boundary.
            // biome-ignore lint/suspicious/noArrayIndexKey: one input per ring, by position
            key={i}
            type="range"
            min={MIN_SCORE}
            max={MAX_SCORE}
            step={SCORE_STEP}
            value={v}
            aria-label={`Ring ${i + 1} starts at`}
            aria-invalid={boundErrors.has(i) || undefined}
            onChange={(e) => onBound(i, Number(e.target.value))}
            className={`pointer-events-none absolute inset-x-0 bottom-0 m-0 h-5 w-full appearance-none bg-transparent focus-visible:outline-none ${RANGE_THUMB}`}
            data-testid={`bullseye-bound-${i}`}
          />
        ))}
      </div>
      <div
        className="-mt-1 flex justify-between text-caption tabular-nums text-text-muted"
        aria-hidden="true"
      >
        <span>1.0</span>
        <span>1.5</span>
        <span>2.0</span>
        <span>2.5</span>
        <span>3.0</span>
      </div>

      <ul className="flex flex-wrap gap-1.5" data-testid="bullseye-bound-chips">
        {bounds.map((v, i) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: one chip per ring, by position
            key={i}
            className={`inline-flex items-center gap-1.5 rounded-pill border px-2 py-0.5 text-label tabular-nums text-text ${
              boundErrors.has(i) ? "border-error-strong" : "border-border"
            }`}
          >
            <span className={`inline-block h-2 w-2 rounded-full ${RING_BG[ringTone(i, k)]}`} />
            Ring {i + 1} from {f2(v)}
          </li>
        ))}
      </ul>
      {[...boundErrors].map(([i, msg]) => (
        <p
          key={`err-${i}`}
          className="text-caption text-error-ink"
          data-testid="bullseye-bound-error"
        >
          Ring {i + 1}: {msg}
        </p>
      ))}

      <button
        type="button"
        onClick={onReset}
        className="self-start text-label text-accent-ink underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        data-testid="bullseye-bounds-reset"
      >
        Reset to the default boundaries
      </button>
      {saved && !saved.is_default && !present && (
        <p className="text-caption text-text-secondary" data-testid="bullseye-set-by">
          Boundaries set by {nameFromEmail(saved.updated_by)}
          {saved.updated_at ? ` · ${monthDay(saved.updated_at)}` : ""}
        </p>
      )}
      <p className="text-caption text-text-muted">
        Weighted AI score from 1 to 3. The bars show how many people sit at each score.
      </p>
    </section>
  );
}
